import { Button } from '@/components/common/button';
import { LoadingSpinner } from '@/components/common/loading_spinner';
import {
  ArrowLeft,
  Download,
  Printer,
  CheckCircle,
  Calendar,
  User,
  FileText,
  CreditCard,
  Building,
  Mail,
  Phone,
  MapPin,
  Hash,
  Droplets,
  Banknote,
  Smartphone,
  Receipt as ReceiptIcon
} from 'lucide-react';
import { PaymentReceipt as PaymentReceiptType } from '@/types/payment_types';

interface PaymentReceiptProps {
  receipt: PaymentReceiptType | null;
  on_download: (receipt: PaymentReceiptType) => void;
  on_print: () => void;
  on_back: () => void;
  is_loading?: boolean;
}

export const PaymentReceipt: React.FC<PaymentReceiptProps> = ({
  receipt,
  on_download,
  on_print,
  on_back,
  is_loading = false
}) => {
  if (is_loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading receipt..." />
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <FileText size={48} className="mx-auto text-medium-gray mb-4" />
          <p className="text-medium-gray">Receipt not found</p>
          <Button variant="primary" onClick={on_back} className="mt-4">
            Back to Payments
          </Button>
        </div>
      </div>
    );
  }

  const { payment, company_details, client_details, allocations } = receipt;

  const format_currency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `KES ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const format_date = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const get_payment_method_label = (method: string) => {
    const method_lower = method.toLowerCase();
    const labels: Record<string, string> = {
      cash: 'Cash Payment',
      mpesa: 'M-Pesa',
      bank_transfer: 'Bank Transfer',
      cheque: 'Cheque Payment',
      card: 'Card Payment'
    };
    return labels[method_lower] || method;
  };

  const get_payment_method_icon = (method: string) => {
    const method_lower = method.toLowerCase();
    const icons: Record<string, React.ReactNode> = {
      cash: <Banknote size={20} className="text-success" />,
      bank_transfer: <Building size={20} className="text-info" />,
      mpesa: <Smartphone size={20} className="text-forest-green" />,
      cheque: <ReceiptIcon size={20} className="text-warning" />,
      card: <CreditCard size={20} className="text-forest-green" />
    };
    return icons[method_lower] || <CreditCard size={20} className="text-medium-gray" />;
  };

  const get_status_badge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-success bg-opacity-10 text-success border-success',
      pending: 'bg-warning bg-opacity-10 text-warning border-warning',
      failed: 'bg-error bg-opacity-10 text-error border-error',
      refunded: 'bg-medium-gray bg-opacity-10 text-medium-gray border-medium-gray'
    };

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold border ${styles[status] || styles.pending}`}>
        {status === 'completed' && <CheckCircle size={16} />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white/95 border-b border-forest-green sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={on_back}
                icon_left={<ArrowLeft size={18} />}
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-charcoal">Payment Receipt</h1>
                <p className="text-sm text-medium-gray mt-0.5">
                  {receipt.receipt_number || payment.payment_number}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={on_print}
                icon_left={<Printer size={18} />}
              >
                Print
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => on_download(receipt)}
                icon_left={<Download size={18} />}
              >
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-md border border-light-gray overflow-hidden print:shadow-none">
          {/* Receipt Header with Gradient */}
          <div className="bg-gradient-to-r from-forest-green to-dark-forest text-white p-8">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  {company_details?.logo_url ? (
                    <img
                      src={company_details.logo_url}
                      alt={company_details.name || 'Company'}
                      className="h-12 w-auto"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-lime-green text-forest-green flex items-center justify-center">
                      <Droplets size={24} />
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold">
                      {company_details?.name || 'Water Management System'}
                    </h2>
                    {company_details?.address && (
                      <p className="text-sm opacity-90 mt-1">{company_details.address}</p>
                    )}
                    {(company_details?.phone || company_details?.email) && (
                      <p className="text-sm opacity-90">
                        {company_details.phone && company_details.phone}
                        {company_details.phone && company_details.email && ' • '}
                        {company_details.email && company_details.email}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-lime-green text-lg font-semibold mt-2">
                  {receipt.receipt_number || payment.payment_number}
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm opacity-90 mb-1">Payment Date</div>
                <div className="text-lg font-semibold">{format_date(receipt.issued_date || payment.payment_date)}</div>
              </div>
            </div>

            {/* Status Badge */}
            <div className="mt-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-md">
                <CheckCircle size={20} />
                <span className="font-semibold">Payment Confirmed</span>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8 space-y-8">
            {/* Payment Amount - Highlighted */}
            <div className="bg-forest-green bg-opacity-5 border-2 border-forest-green rounded-md p-6 text-center">
              <p className="text-sm font-medium text-medium-gray mb-2">Amount Paid</p>
              <p className="text-4xl font-bold text-forest-green">
                {format_currency(payment.amount)}
              </p>
              <div className="mt-4">
                {get_status_badge(payment.status)}
              </div>
            </div>

            {/* Client Information */}
            <div>
              <h3 className="text-lg font-bold text-charcoal mb-4 flex items-center gap-2">
                <User size={20} className="text-forest-green" />
                Client Information
              </h3>
              <div className="bg-off-white rounded-md p-5 border border-light-gray">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center flex-shrink-0">
                      <User size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-medium-gray font-medium mb-1">Client Name</p>
                      <p className="text-sm font-semibold text-charcoal">{client_details?.name || payment.client_name}</p>
                    </div>
                  </div>

                  {client_details?.email && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-md bg-info bg-opacity-10 text-info flex items-center justify-center flex-shrink-0">
                        <Mail size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-medium-gray font-medium mb-1">Email</p>
                        <p className="text-sm font-semibold text-charcoal">{client_details.email}</p>
                      </div>
                    </div>
                  )}

                  {client_details?.phone && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-md bg-success bg-opacity-10 text-success flex items-center justify-center flex-shrink-0">
                        <Phone size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-medium-gray font-medium mb-1">Phone</p>
                        <p className="text-sm font-semibold text-charcoal">{client_details.phone}</p>
                      </div>
                    </div>
                  )}

                  {client_details?.address && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-md bg-warning bg-opacity-10 text-warning flex items-center justify-center flex-shrink-0">
                        <MapPin size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-medium-gray font-medium mb-1">Address</p>
                        <p className="text-sm font-semibold text-charcoal">{client_details.address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            {payment.invoice_number && (
              <div>
                <h3 className="text-lg font-bold text-charcoal mb-4 flex items-center gap-2">
                  <FileText size={20} className="text-forest-green" />
                  Invoice Details
                </h3>
                <div className="bg-off-white rounded-lg p-5 border border-light-gray">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-medium-gray font-medium mb-1">Invoice Number</p>
                      <p className="text-sm font-semibold text-charcoal">{payment.invoice_number}</p>
                    </div>
                  </div>

                  {/* Payment Allocations */}
                  {allocations && allocations.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-light-gray">
                      <p className="text-xs text-medium-gray font-medium mb-3">Payment Allocation</p>
                      <div className="space-y-2">
                        {allocations.map((alloc, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm">
                            <div>
                              <span className="text-charcoal font-medium">{alloc.invoice_number}</span>
                              <span className="text-xs text-medium-gray ml-2">{format_date(alloc.invoice_date)}</span>
                            </div>
                            <span className="font-semibold text-forest-green">
                              {format_currency(alloc.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Details */}
            <div>
              <h3 className="text-lg font-bold text-charcoal mb-4 flex items-center gap-2">
                <CreditCard size={20} className="text-forest-green" />
                Payment Details
              </h3>
              <div className="bg-off-white rounded-lg p-5 border border-light-gray">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center flex-shrink-0">
                      {get_payment_method_icon(payment.payment_method)}
                    </div>
                    <div>
                      <p className="text-xs text-medium-gray font-medium mb-1">Payment Method</p>
                      <p className="text-sm font-semibold text-charcoal">{get_payment_method_label(payment.payment_method)}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-info bg-opacity-10 text-info flex items-center justify-center flex-shrink-0">
                      <Calendar size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-medium-gray font-medium mb-1">Payment Date</p>
                      <p className="text-sm font-semibold text-charcoal">{format_date(payment.payment_date)}</p>
                    </div>
                  </div>

                  {payment.reference_number && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-md bg-warning bg-opacity-10 text-warning flex items-center justify-center flex-shrink-0">
                        <Hash size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-medium-gray font-medium mb-1">Reference Number</p>
                        <p className="text-sm font-semibold text-charcoal">{payment.reference_number}</p>
                      </div>
                    </div>
                  )}

                  {payment.mpesa_receipt_number && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-md bg-success bg-opacity-10 text-success flex items-center justify-center flex-shrink-0">
                        <Hash size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-medium-gray font-medium mb-1">M-Pesa Receipt</p>
                        <p className="text-sm font-semibold text-charcoal">{payment.mpesa_receipt_number}</p>
                      </div>
                    </div>
                  )}
                </div>

                {payment.notes && (
                  <div className="mt-4 pt-4 border-t border-light-gray">
                    <p className="text-xs text-medium-gray font-medium mb-2">Notes</p>
                    <p className="text-sm text-charcoal">{payment.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Received By */}
            {payment.recorded_by_name && (
              <div className="bg-off-white rounded-md p-4 border border-light-gray">
                <p className="text-sm text-medium-gray">
                  <span className="font-semibold text-charcoal">Recorded by:</span> {payment.recorded_by_name}
                </p>
              </div>
            )}

            {/* Payment Summary */}
            <div className="border-t-2 border-light-gray pt-6">
              <div className="bg-gradient-to-r from-forest-green to-dark-forest text-white rounded-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-semibold">Total Payment</span>
                  <span className="text-3xl font-bold">{format_currency(payment.amount)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm opacity-90">
                  <CheckCircle size={16} />
                  <span>Payment received and confirmed</span>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="text-center pt-6 border-t border-light-gray">
              <p className="text-sm text-medium-gray mb-2">
                Thank you for your payment!
              </p>
              <p className="text-xs text-medium-gray">
                This is an automatically generated receipt. For any queries, please contact our support team.
              </p>
            </div>
          </div>

          {/* Print Footer */}
          <div className="hidden print:block bg-off-white p-6 text-center border-t border-light-gray">
            <p className="text-xs text-medium-gray">
              Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};